import type {
  CaptureContext,
  EventHint,
  Scope,
  SeverityLevel,
  getCurrentScope,
  init,
  replayIntegration,
  Breadcrumb,
} from "@sentry/vue";
import { noop } from "lodash-es";

import type { UnknownRecord } from "@/utils/type";

export const consoleSentry = {
  init: noop as typeof init,
  setUser: noop,
  getCurrentScope: (() => ({
    setUser: noop,
    setTag: noop,
    getUser: () => undefined,
  })) as typeof getCurrentScope,
  captureException: consoleLogException,
  captureMessage: consoleLogMessage,
  addBreadcrumb: noop,
  replayIntegration: noop as typeof replayIntegration,
  fingerprint: "",
};

export function consoleLogBreadcrumbs(log: boolean) {
  consoleSentry.addBreadcrumb = log ? consoleLogBreadcrumb : noop;
}

/* eslint-disable no-console */

function consoleLogException(exception: unknown, hint?: EventHint) {
  console.error(exception, contexts(hint));
  return "";
}

function consoleLogMessage(message: string, captureContext?: CaptureContext | SeverityLevel) {
  const logFunc = consoleLogger[getLevel(captureContext) ?? "error"] || console.log;
  logFunc(message);
  return "";
}

function consoleLogBreadcrumb(breadcrumb: Breadcrumb) {
  const logFunc = consoleLogger[getLevel(breadcrumb.level) ?? "info"] || console.log;
  logFunc(
    breadcrumb.type ?? "",
    breadcrumb.category ?? "",
    breadcrumb.message ?? "",
    breadcrumb.data ?? "",
  );
}

const consoleLogger: Record<string, (...args: unknown[]) => void> = {
  error: console.error,
  warning: console.warn,
};

/* eslint-enable */

function contexts(hint?: EventHint): UnknownRecord | undefined {
  const ctx = hint?.captureContext;
  return ctx && "contexts" in ctx ? ctx.contexts : undefined;
}

function getLevel(context?: CaptureContext | SeverityLevel): SeverityLevel | undefined {
  if (context === undefined) {
    return undefined;
  }
  if (typeof context === "string") {
    return context;
  }
  if ("level" in context) {
    return context.level;
  }
  if (isScope(context)) {
    return context.getScopeData().level;
  }
}

function isScope(context: CaptureContext): context is Scope {
  return "getScopeData" in context;
}
